import { C } from "./theme.js";

export function Card({ children, style, className }) {
  return (
    <div className={className} style={{ border: `1px solid ${C.line}`, borderRadius: 12, background: C.card, padding: 16, ...style }}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "2px 0 12px" }}>
      <h2 style={{ fontSize: 14, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: 0.2 }}>{children}</h2>
      {right}
    </div>
  );
}

// Estado vacío elegante: la app se ve bien aunque los flujos aún no hayan corrido.
export function EmptyState({ icon = "🛰️", title, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "44px 24px", color: C.muted }}>
      <div style={{ fontSize: 34, marginBottom: 10, opacity: 0.85 }}>{icon}</div>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: C.page, marginBottom: 6 }}>{title}</div>
      {hint && <div style={{ fontSize: 12.5, maxWidth: 460, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

export function Pill({ children, color = C.brand, soft = false }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4,
      padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap",
      color: soft ? color : "#fff", background: soft ? `${color}18` : color, border: soft ? `1px solid ${color}44` : "none",
    }}>{children}</span>
  );
}

// Barra horizontal (para SoV, cita por motor, sentimiento…).
export function Bar({ value, max = 100, color = C.brand, height = 10 }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div style={{ flex: 1, height, background: "#eef1f4", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width .4s ease" }} />
    </div>
  );
}

// Flecha de tendencia para rankings (subir = posición más pequeña = bueno).
export function Trend({ delta }) {
  if (delta == null) return <span style={{ color: C.faint, fontSize: 12 }}>—</span>;
  if (delta === 0) return <span style={{ color: C.muted, fontSize: 12 }}>▬ 0</span>;
  const up = delta > 0;
  return (
    <span style={{ color: up ? C.verde : C.rojo, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
      {up ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}
    </span>
  );
}

// Filtro de idioma en píldoras, compartido por On-page, Salud técnica y Search
// Console. La lista de idiomas se le pasa ya derivada de los datos (idiomasDe),
// no cableada: si solo hay un idioma no se pinta nada.
export function FiltroIdioma({ idiomas, value, onChange, todos = "Todos" }) {
  if (!idiomas || idiomas.length < 2) return null;
  const opciones = [{ id: "todas", label: todos, n: idiomas.reduce((s, i) => s + i.n, 0) }, ...idiomas];
  return (
    <div style={{ display: "inline-flex", gap: 4, padding: 4, background: "#eef0f2", borderRadius: 10 }}>
      {opciones.map((o) => {
        const on = value === o.id;
        return (
          <button key={o.id} onClick={() => onChange(o.id)} disabled={o.n === 0} title={`${o.n} página${o.n !== 1 ? "s" : ""}`} style={{
            fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 7, border: "none",
            cursor: o.n === 0 ? "default" : "pointer", opacity: o.n === 0 ? 0.45 : 1,
            background: on ? "#fff" : "transparent", color: on ? C.brand : C.muted,
            boxShadow: on ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
          }}>{o.label} <span style={{ color: on ? C.muted : C.faint, fontWeight: 800 }}>{o.n}</span></button>
        );
      })}
    </div>
  );
}

// Tabla simple reutilizable. Th admite orden (onClick + active/dir).
const TB = "1px solid #eaecef";
export function Th({ children, align = "left", w, onClick, active, dir }) {
  const clickable = !!onClick;
  return (
    <th onClick={onClick} style={{ borderBottom: `1.5px solid ${C.line}`, background: "#f7f8fa", padding: "8px 10px", fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, color: active ? C.brand : C.muted, textAlign: align, position: "sticky", top: 0, width: w, whiteSpace: "nowrap", cursor: clickable ? "pointer" : "default", userSelect: "none" }}>
      {children}
      {active ? <span style={{ color: C.brand }}>{dir === "asc" ? " ▲" : " ▼"}</span> : (clickable ? <span style={{ color: C.gris }}> ⇅</span> : null)}
    </th>
  );
}
export function Td({ children, align = "left", strong, color, nowrap = true }) {
  return <td style={{ borderBottom: TB, padding: "8px 10px", fontSize: 12.5, textAlign: align, color: color || C.page, fontWeight: strong ? 700 : 500, whiteSpace: nowrap ? "nowrap" : "normal" }}>{children}</td>;
}
export function TableWrap({ children, maxHeight = 620, minWidth = 480 }) {
  return (
    <div style={{ overflow: "auto", maxHeight, border: `1px solid ${C.line}`, borderRadius: 10 }}>
      <table style={{ width: "100%", minWidth }}>{children}</table>
    </div>
  );
}
